import React, { useState, useEffect } from "react";
import { Line, Doughnut, HorizontalBar } from "react-chartjs-2";
import {
    Card, CardHeader, CardBody, CardFooter, CardTitle,
    Row, Col, Table
} from "reactstrap";
import moment from "moment";
import { useHistory } from "react-router-dom";

import UserService from '../services/user.service';

const COLORS = {
    teal: '#51cbce',
    green: '#6bd098',
    orange: '#ffa534',
    red: '#ef8157',
    blue: '#51bcda',
    purple: '#9b59b6',
    dark: '#333',
    gray: '#888',
    lightBg: '#f9f9f9',
};

const PIE_COLORS = ['#51cbce', '#6bd098', '#ffa534', '#ef8157', '#51bcda'];

const formatCurrency = (value) => {
    return new Intl.NumberFormat('vi-VN').format(value) + ' đ';
};

function Dashboard({ selectedMonth }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [orderDate, setOrderDate] = useState(new Date());
    const [dayOrders, setDayOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const history = useHistory();

    const fetchOrdersByDate = (date) => {
        setLoadingOrders(true);
        const dateStr = moment(date).format('YYYY-MM-DD');
        UserService.getOrdersByDate(dateStr)
            .then(response => {
                setDayOrders(response.data);
                setLoadingOrders(false);
            })
            .catch(() => {
                setDayOrders([]);
                setLoadingOrders(false);
            });
    };

    const changeDate = (offset) => {
        const newDate = new Date(orderDate);
        newDate.setDate(newDate.getDate() + offset);
        if (newDate > new Date()) return; // Không cho chọn tương lai
        setOrderDate(newDate);
        fetchOrdersByDate(newDate);
    };

    const isToday = moment(orderDate).isSame(moment(), 'day');

    useEffect(() => {
        setLoading(true);
        UserService.getReport(selectedMonth)
            .then(response => {
                setData(response.data);
                setDayOrders(response.data.recent_orders || []);
                setLoading(false);
            })
            .catch(error => {
                console.error("Dashboard error:", error);
                setLoading(false);
            });
    }, [selectedMonth]);

    if (loading) {
        return (
            <div className="content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <div style={{ textAlign: 'center' }}>
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: '40px', color: COLORS.teal }}></i>
                    <p style={{ marginTop: '15px', color: COLORS.gray }}>Đang tải dữ liệu...</p>
                </div>
            </div>
        );
    }

    if (!data) return <div className="content"><p>Không thể tải dữ liệu dashboard</p></div>;

    // Line chart config
    const lineChartData = {
        labels: data.chart?.key || [],
        datasets: [{
            label: 'Doanh thu (nghìn đ)',
            data: data.chart?.value || [],
            fill: true,
            backgroundColor: 'rgba(81, 203, 206, 0.1)',
            borderColor: COLORS.teal,
            pointBorderColor: COLORS.teal,
            pointBackgroundColor: '#fff',
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBorderWidth: 2,
            tension: 0.4,
        }]
    };
    const lineChartOptions = {
        legend: { display: false },
        scales: {
            yAxes: [{ ticks: { beginAtZero: true, callback: (v) => v.toLocaleString('vi-VN') } }],
            xAxes: [{ gridLines: { display: false } }]
        },
        tooltips: {
            callbacks: { label: (item) => new Intl.NumberFormat('vi-VN').format(item.yLabel * 1000) + ' đ' }
        }
    };

    // Doughnut chart config
    const pmLabels = Object.keys(data.payment_methods || {});
    const pmValues = Object.values(data.payment_methods || {});
    const doughnutData = {
        labels: pmLabels,
        datasets: [{
            data: pmValues,
            backgroundColor: PIE_COLORS.slice(0, pmLabels.length),
            borderWidth: 2,
            borderColor: '#fff',
        }]
    };
    const doughnutOptions = {
        legend: { position: 'bottom', labels: { padding: 15, usePointStyle: true, fontSize: 13 } },
        cutoutPercentage: 65,
    };

    // Horizontal bar chart config
    const topTools = data.top_tools || [];
    const barData = {
        labels: topTools.map(t => t.name?.replace('DHB ', '') || ''),
        datasets: [{
            label: 'Doanh thu (đ)',
            data: topTools.map(t => t.revenue),
            backgroundColor: PIE_COLORS.slice(0, topTools.length),
            borderRadius: 5,
            barThickness: 22,
        }]
    };
    const barOptions = {
        legend: { display: false },
        scales: {
            xAxes: [{ ticks: { beginAtZero: true, callback: (v) => (v / 1000000).toFixed(1) + ' tr' } }],
            yAxes: [{ gridLines: { display: false } }]
        },
        tooltips: {
            callbacks: { label: (item) => formatCurrency(item.xLabel) }
        }
    };

    // Status color
    const getStatusStyle = (status) => {
        if (status === 'completed') return { backgroundColor: '#28a745', color: '#fff' };
        return { backgroundColor: COLORS.orange, color: '#fff' };
    };

    // Growth indicator
    const growthValue = data.growth || 0;
    const growthColor = growthValue >= 0 ? COLORS.green : COLORS.red;
    const growthIcon = growthValue >= 0 ? 'fa-arrow-up' : 'fa-arrow-down';

    return (
        <div className="content">
            <style>{`@media (max-width: 767px) { .dashboard-date-nav { margin: 0 auto !important; padding-top: 10px; } }`}</style>
            {/* Row 1: Stat Cards */}
            <Row>
                <Col lg="4" md="6" sm="6">
                    <Card className="card-stats" style={{ borderRadius: '15px' }}>
                        <CardBody style={{ padding: '20px 25px' }}>
                            <Row>
                                <Col md="4" xs="4">
                                    <div style={{
                                        width: '50px', height: '50px', borderRadius: '12px',
                                        backgroundColor: 'rgba(107, 208, 152, 0.15)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        <i className="fas fa-money-bill-wave" style={{ fontSize: '22px', color: COLORS.green }} />
                                    </div>
                                </Col>
                                <Col md="8" xs="8">
                                    <div className="numbers">
                                        <p className="card-category" style={{ color: COLORS.gray, fontSize: '16px', marginBottom: '5px' }}>
                                            {isToday ? 'Doanh thu hôm nay' : 'Doanh thu ' + moment(orderDate).format('DD/MM')}
                                        </p>
                                        <CardTitle tag="p" style={{ fontSize: '22px', fontWeight: 'bold', color: COLORS.dark }}>
                                            {formatCurrency(dayOrders.filter(o => o.paymentStatus === 'completed').reduce((sum, o) => sum + (o.totalPrice || 0), 0))}
                                        </CardTitle>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                        <CardFooter>
                            <hr />
                            <div className="stats" style={{ color: COLORS.gray }}>
                                <i className="fas fa-check-circle" style={{ color: COLORS.green, marginRight: '5px' }} />
                                {dayOrders.filter(o => o.paymentStatus === 'completed').length} đơn hoàn thành
                            </div>
                        </CardFooter>
                    </Card>
                </Col>

                <Col lg="4" md="6" sm="6">
                    <Card className="card-stats" style={{ borderRadius: '15px' }}>
                        <CardBody style={{ padding: '20px 25px' }}>
                            <Row>
                                <Col md="4" xs="5">
                                    <div style={{
                                        width: '50px', height: '50px', borderRadius: '12px',
                                        backgroundColor: 'rgba(81, 188, 218, 0.15)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        <i className="fas fa-chart-line" style={{ fontSize: '22px', color: COLORS.blue }} />
                                    </div>
                                </Col>
                                <Col md="8" xs="7">
                                    <div className="numbers">
                                        <p className="card-category" style={{ color: COLORS.gray, fontSize: '16px', marginBottom: '5px' }}>Doanh thu tháng</p>
                                        <CardTitle tag="p" style={{ fontSize: '22px', fontWeight: 'bold', color: COLORS.dark }}>{formatCurrency(data.total_sales_raw || 0)}</CardTitle>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                        <CardFooter>
                            <hr />
                            <div className="stats" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>
                                    <i className={`fas ${growthIcon}`} style={{ color: growthColor, marginRight: '5px' }} />
                                    <span style={{ color: growthColor, fontWeight: 'bold' }}>{growthValue >= 0 ? '+' : ''}{growthValue}%</span>
                                    <span style={{ color: COLORS.gray }}> so tháng trước</span>
                                </span>
                                <span style={{ color: COLORS.gray }}>
                                    <i className="fas fa-check-circle" style={{ color: COLORS.green, marginRight: '5px' }} />
                                    {data.total_orders} đơn
                                </span>
                            </div>
                        </CardFooter>
                    </Card>
                </Col>

                <Col lg="4" md="6" sm="6">
                    <Card className="card-stats" style={{ borderRadius: '15px' }}>
                        <CardBody style={{ padding: '20px 25px' }}>
                            <Row>
                                <Col md="4" xs="5">
                                    <div style={{
                                        width: '50px', height: '50px', borderRadius: '12px',
                                        backgroundColor: 'rgba(239, 129, 87, 0.15)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        <i className="fas fa-file-invoice-dollar" style={{ fontSize: '22px', color: COLORS.red }} />
                                    </div>
                                </Col>
                                <Col md="8" xs="7">
                                    <div className="numbers">
                                        <p className="card-category" style={{ color: COLORS.gray, fontSize: '16px', marginBottom: '5px' }}>Thuế tháng này</p>
                                        <CardTitle tag="p" style={{ fontSize: '22px', fontWeight: 'bold', color: COLORS.red }}>
                                            {formatCurrency(Math.round((data.total_sales_raw || 0) * 0.07))}
                                        </CardTitle>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                        <CardFooter>
                            <hr />
                            <div className="stats" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: COLORS.gray }}>
                                    GTGT 5%: <strong>{formatCurrency(Math.round((data.total_sales_raw || 0) * 0.05))}</strong>
                                </span>
                                <span style={{ color: COLORS.gray }}>
                                    TNCN 2%: <strong>{formatCurrency(Math.round((data.total_sales_raw || 0) * 0.02))}</strong>
                                </span>
                            </div>
                        </CardFooter>
                    </Card>
                </Col>
            </Row>

            {/* Row 2: Today Orders & Top Tools */}
            <Row>
                <Col md="6">
                    <Card style={{ borderRadius: '15px', padding: '10px' }}>
                        <CardHeader style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{
                                    width: '44px', height: '44px', borderRadius: '12px',
                                    backgroundColor: 'rgba(81,203,206,0.12)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    marginRight: '12px', flexShrink: 0
                                }}>
                                    <i className="fas fa-shopping-bag" style={{ fontSize: '20px', color: COLORS.teal }}></i>
                                </div>
                                <div>
                                    <CardTitle tag="h5" style={{ fontWeight: 'bold', color: COLORS.dark, margin: 0, whiteSpace: 'nowrap' }}>
                                        Đơn hàng {isToday ? 'hôm nay' : moment(orderDate).format('DD/MM/YYYY')}
                                    </CardTitle>
                                    <span style={{ color: COLORS.gray, fontSize: '13px' }}>
                                        {dayOrders.length} đơn hàng
                                    </span>
                                </div>
                            </div>
                            <div className="dashboard-date-nav" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <button
                                    onClick={() => changeDate(-1)}
                                    style={{
                                        border: 'none', background: 'rgba(81,203,206,0.12)', borderRadius: '8px',
                                        width: '32px', height: '32px', cursor: 'pointer', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', color: COLORS.teal, fontSize: '14px'
                                    }}
                                >
                                    <i className="fas fa-chevron-left"></i>
                                </button>
                                <span style={{ fontSize: '13px', color: COLORS.dark, fontWeight: '500', minWidth: '70px', textAlign: 'center' }}>
                                    {isToday ? 'Hôm nay' : moment(orderDate).format('DD/MM')}
                                </span>
                                <button
                                    onClick={() => changeDate(1)}
                                    disabled={isToday}
                                    style={{
                                        border: 'none', background: isToday ? '#eee' : 'rgba(81,203,206,0.12)', borderRadius: '8px',
                                        width: '32px', height: '32px', cursor: isToday ? 'not-allowed' : 'pointer', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', color: isToday ? '#ccc' : COLORS.teal, fontSize: '14px'
                                    }}
                                >
                                    <i className="fas fa-chevron-right"></i>
                                </button>
                            </div>
                        </CardHeader>
                        <CardBody style={{ padding: '20px 15px 15px' }}>
                            {loadingOrders ? (
                                <p style={{ color: COLORS.gray, textAlign: 'center', padding: '20px 0' }}>
                                    <i className="fas fa-spinner fa-spin" style={{ marginRight: '6px' }}></i>Đang tải...
                                </p>
                            ) : dayOrders.length > 0 ? (
                                <>
                                    {/* Desktop Table */}
                                    <div className="d-none d-md-block">
                                        <Table responsive style={{ marginBottom: 0 }}>
                                            <thead>
                                                <tr style={{ fontSize: '13px', color: COLORS.gray }}>
                                                    <th style={{ border: 'none', padding: '8px 10px' }}>Mã đơn</th>
                                                    <th style={{ border: 'none', padding: '8px 10px' }}>Khách hàng</th>
                                                    <th style={{ border: 'none', padding: '8px 10px' }}>Trạng thái</th>
                                                    <th style={{ border: 'none', padding: '8px 10px', textAlign: 'center' }}>KiotViet</th>
                                                    <th style={{ border: 'none', padding: '8px 10px', textAlign: 'right' }}>Tổng tiền</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {dayOrders.map((order, idx) => (
                                                    <tr key={idx}
                                                        onClick={() => history.push('/orders-id/' + order.id)}
                                                        style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                                                        className="recent-order-row"
                                                    >
                                                        <td style={{ padding: '10px', verticalAlign: 'middle' }}>
                                                            <strong style={{ color: COLORS.teal }}>#{order.id}</strong>
                                                            <br />
                                                            <span style={{ fontSize: '13px', color: COLORS.gray }}>
                                                                {moment(order.createdAt).format('HH:mm')}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: '10px', verticalAlign: 'middle' }}>
                                                            <span style={{ fontSize: '13px', fontWeight: '500' }}>{order.fullName || '—'}</span>
                                                            <br />
                                                            <span style={{ fontSize: '12px', color: COLORS.gray }}>{order.email || ''}</span>
                                                        </td>
                                                        <td style={{ padding: '10px', verticalAlign: 'middle' }}>
                                                            <span style={{
                                                                ...getStatusStyle(order.paymentStatus),
                                                                padding: '4px 12px',
                                                                borderRadius: '20px',
                                                                fontSize: '11px',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {order.paymentStatus === 'completed' ? 'Hoàn thành' : 'Chờ thanh toán'}
                                                            </span>
                                                            {order.handleBy && (
                                                                <div style={{ fontSize: '11px', color: COLORS.gray, marginTop: '4px' }}>
                                                                    <i className="fas fa-user" style={{ marginRight: '3px', fontSize: '10px' }}></i>
                                                                    Xử lý : {order.handleBy}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td style={{ padding: '10px', verticalAlign: 'middle', textAlign: 'center' }}>
                                                            {order.paymentStatus === 'completed' ? (
                                                                order.syncKiot ? (
                                                                    <span style={{ backgroundColor: '#28a745', color: '#fff', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>Đã sync</span>
                                                                ) : (
                                                                    <span style={{ backgroundColor: '#ff9800', color: '#fff', padding: '4px 10px', borderRadius: '12px', fontSize: '11px' }}>Chưa sync</span>
                                                                )
                                                            ) : (
                                                                <span style={{ color: '#ccc' }}>—</span>
                                                            )}
                                                        </td>
                                                        <td style={{ padding: '10px', verticalAlign: 'middle', textAlign: 'right', fontWeight: 'bold', fontSize: '15px' }}>
                                                            {formatCurrency(order.totalPrice || 0)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>

                                    {/* Mobile Cards */}
                                    <div className="d-block d-md-none">
                                        {dayOrders.map((order, idx) => (
                                            <div key={idx}
                                                onClick={() => history.push('/orders-id/' + order.id)}
                                                style={{
                                                    cursor: 'pointer',
                                                    padding: '12px 6px',
                                                    borderBottom: idx < dayOrders.length - 1 ? '1px solid #e4e4e4ff' : 'none',
                                                }}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                                                    <div>
                                                        <strong style={{ color: COLORS.teal, fontSize: '14px' }}>#{order.id}</strong>
                                                        <span style={{ fontSize: '12px', color: COLORS.gray, marginLeft: '8px' }}>
                                                            {moment(order.createdAt).format('HH:mm')}
                                                        </span>
                                                    </div>
                                                    {order.handleBy ? (
                                                        <span style={{ fontSize: '12px', color: COLORS.gray }}>
                                                            <i className="fas fa-user" style={{ marginRight: '3px', fontSize: '10px' }}></i>
                                                            {order.handleBy}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                <div style={{ marginBottom: '6px' }}>
                                                    <div style={{ fontSize: '13px', fontWeight: '500' }}>{order.fullName || '—'}</div>
                                                    <div style={{ fontSize: '12px', color: COLORS.gray }}>{order.email || ''}</div>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                    <span style={{
                                                        ...getStatusStyle(order.paymentStatus),
                                                        padding: '3px 10px',
                                                        borderRadius: '12px',
                                                        fontSize: '11px',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {order.paymentStatus === 'completed' ? 'Hoàn thành' : 'Chờ thanh toán'}
                                                    </span>
                                                    {order.paymentStatus === 'completed' && (
                                                        order.syncKiot ? (
                                                            <span style={{ backgroundColor: '#28a745', color: '#fff', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>Đã sync</span>
                                                        ) : (
                                                            <span style={{ backgroundColor: '#ff9800', color: '#fff', padding: '3px 10px', borderRadius: '12px', fontSize: '11px' }}>Chưa sync</span>
                                                        )
                                                    )}
                                                    <strong style={{ fontSize: '13px', marginLeft: 'auto' }}>{formatCurrency(order.totalPrice || 0)}</strong>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <p style={{ color: COLORS.gray, textAlign: 'center', padding: '20px 0' }}>
                                    {isToday ? 'Hôm nay chưa có đơn hàng' : 'Không có đơn hàng ngày ' + moment(orderDate).format('DD/MM/YYYY')}
                                </p>
                            )}
                        </CardBody>
                    </Card>
                </Col>

                <Col md="6">
                    <Card style={{ borderRadius: '15px', padding: '10px' }}>
                        <CardHeader>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{
                                    width: '44px', height: '44px', borderRadius: '12px',
                                    backgroundColor: 'rgba(255,165,52,0.12)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    marginRight: '12px', flexShrink: 0
                                }}>
                                    <i className="fas fa-trophy" style={{ fontSize: '20px', color: COLORS.orange }}></i>
                                </div>
                                <div>
                                    <CardTitle tag="h5" style={{ fontWeight: 'bold', color: COLORS.dark, margin: 0 }}>
                                        Phần mềm bán chạy
                                    </CardTitle>
                                    <span style={{ color: COLORS.gray, fontSize: '13px' }}>Tháng này (theo doanh thu)</span>
                                </div>
                            </div>
                        </CardHeader>
                        <CardBody style={{ paddingTop: '5px' }}>
                            {topTools.length > 0 ? (
                                <HorizontalBar data={barData} options={barOptions} height={180} />
                            ) : (
                                <p style={{ color: COLORS.gray, textAlign: 'center' }}>Chưa có dữ liệu</p>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Row 3: Revenue Chart */}
            <Row>
                <Col md="12">
                    <Card className="card-chart" style={{ borderRadius: '15px', padding: '10px' }}>
                        <CardHeader>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{
                                    width: '44px', height: '44px', borderRadius: '12px',
                                    backgroundColor: 'rgba(81,203,206,0.12)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    marginRight: '12px', flexShrink: 0
                                }}>
                                    <i className="fas fa-chart-area" style={{ fontSize: '20px', color: COLORS.teal }}></i>
                                </div>
                                <div>
                                    <CardTitle tag="h5" style={{ fontWeight: 'bold', color: COLORS.dark, margin: 0 }}>
                                        Doanh thu tháng này
                                    </CardTitle>
                                    <span style={{ color: COLORS.gray, fontSize: '13px' }}>Đơn vị: nghìn đồng</span>
                                </div>
                            </div>
                        </CardHeader>
                        <CardBody>
                            <Line data={lineChartData} options={lineChartOptions} {...(window.innerWidth >= 768 ? { height: 60 } : {})} />
                        </CardBody>
                        <CardFooter>
                            <hr />
                            <div className="card-stats" style={{ color: COLORS.gray }}>
                                <i className="fas fa-info-circle" style={{ marginRight: '4px' }} />
                                TB: <strong>{data.average_sales}</strong>/ngày
                            </div>
                        </CardFooter>
                    </Card>
                </Col>
            </Row>
        </div >
    );
}

export default Dashboard;
